/*
 * Painting offer: line stamping engine.
 *
 * Reads the arm from the cart attribute `offer_test_arm` (A, the one offer
 * left since the test ended), works out which painting lines get a discount,
 * and stamps those lines with the hidden line property `_offer`. Offer A
 * stamps whole lines, so a line is never split. The rule itself lives in
 * offer-test-rule.js.
 *
 * Runs on page load, after every cart change (the side cart's cart watcher
 * calls `omdOfferStamps.schedule()`), after the arm script writes the arm
 * on the cart, and just before checkout. A cart that carries another arm
 * than the device shows gets the device's arm first, then its stamps.
 *
 * Shopify keeps only one of two cart writes that are in flight at once, and
 * answers both with 200. So this engine and the free-gift engine
 * (free-gift.js) take turns through one shared lock, `window.omdCartLock`.
 * The gift engine also waits for `omdOfferStamps.idle()`, so it decides on
 * the stamped total.
 */
(function (root, factory) {
  // One lock for the page's background cart writers. Each holds it for a
  // whole pass. free-gift.js carries the same function (a test checks they
  // match); whichever loads first creates the lock.
  function cartLock(win) {
    // Shopify keeps only one of two cart writes in flight at once, so the
    // lock is never let go while its holder has a cart request in flight.
    // HOLD_CEILING only lets go of a holder that is waiting on nothing. A
    // holder that was let go starts no new request; its engine runs again
    // later and reads the cart before any write.
    //
    // A request still in flight after REQUEST_CEILING keeps the lock too.
    // Instead, everyone waiting gives up and runs nothing, and so does anyone
    // who asks while it is still in flight. The error they get carries
    // `settled`, which resolves once that request is done.
    var HOLD_CEILING = 15000;
    var REQUEST_CEILING = 30000;
    if (!win.omdCartLock) {
      var tail = Promise.resolve();
      var waiting = [];
      // Requests in flight past REQUEST_CEILING, and when they are all done.
      var stuck = 0;
      var unstick = null;
      var settled = null;
      win.omdCartLock = function (task) {
        var release;
        var released = new Promise(function (resolve) { release = resolve; });
        var stop;
        var stopped = new Promise(function (resolve, reject) { stop = reject; });
        var gaveUp = false;
        var giveUp = function () {
          if (gaveUp) return;
          gaveUp = true;
          waiting.splice(waiting.indexOf(giveUp), 1);
          var error = new Error('cart request hung');
          error.gaveUp = true;
          error.settled = settled;
          stop(error);
        };
        waiting.push(giveUp);
        var turn = tail.then(function () {
          // Gave up while waiting: pass the turn straight on.
          if (gaveUp) return release();
          waiting.splice(waiting.indexOf(giveUp), 1);
          var busy = 0;
          var overdue = false;
          var ceiling = null;
          var holder = { gone: false };
          var letGo = function () {
            if (holder.gone) return;
            holder.gone = true;
            win.clearTimeout(ceiling);
            release();
          };
          var track = function (start, timed) {
            if (holder.gone) {
              var error = new Error('cart lock let go');
              error.released = true;
              return Promise.reject(error);
            }
            busy += 1;
            var late = false;
            var hung = timed ? win.setTimeout(function () {
              late = true;
              if (!stuck++) settled = new Promise(function (resolve) { unstick = resolve; });
              waiting.slice().forEach(function (quit) { quit(); });
            }, REQUEST_CEILING) : null;
            var settle = function () {
              if (hung !== null) win.clearTimeout(hung);
              if (late && !--stuck) unstick();
              busy -= 1;
              if (overdue && !busy) letGo();
            };
            var work = Promise.resolve().then(start);
            work.then(settle, settle);
            return work;
          };
          // One cart request.
          holder.request = function (start) { return track(start, true); };
          // Requests that belong together, e.g. a split's lower and add.
          holder.group = function (start) { return track(start, false); };
          ceiling = win.setTimeout(function () {
            overdue = true;
            if (!busy) letGo();
          }, HOLD_CEILING);
          var work = Promise.resolve().then(function () { return task(holder); });
          // A task that ends with a request still in flight keeps the lock
          // until that request is done.
          var finish = function () {
            if (busy) overdue = true;
            else letGo();
          };
          work.then(finish, finish);
          return work;
        });
        tail = released;
        if (stuck) giveUp();
        return Promise.race([turn, stopped]);
      };
    }
    return win.omdCartLock;
  }
  factory.cartLock = cartLock;

  if (typeof module === 'object' && module.exports) module.exports = factory;
  else root.OmdOfferStamps = factory;

  if (typeof window !== 'undefined' && window.OMD_OFFER_TEST) {
    var makeEngine = function () {
      return factory({
        fetch: window.fetch.bind(window),
        rule: window.OmdOfferRule,
        config: window.OMD_OFFER_TEST,
        root: (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/',
        setTimeout: window.setTimeout.bind(window),
        clearTimeout: window.clearTimeout.bind(window),
        lock: cartLock(window),
        // The arm this device shows, and the check that writes it on the
        // cart (snippets/offer-test-arm.liquid). Read when needed.
        arm: function () { return window.omdOfferArm || null; },
        refreshCart: function () {
          if (window.sideCart && window.sideCart.refresh) return window.sideCart.refresh();
          return null;
        }
      });
    };
    var start = function () {
      var engine = makeEngine();
      window.omdOfferStamps = engine;
      engine.bindPage(window, document);
      engine.schedule();
    };
    var whenReady = function (fn) {
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
      else fn();
    };
    if (!window.OMD_OFFER_TEST.active) {
      // No cart cookie means no cart, so there is nothing to clear.
      if (/(?:^|; )cart=/.test(document.cookie)) whenReady(function () { makeEngine().clearStamps(); });
    } else {
      // The page speed script starts deferred scripts in no fixed order, so
      // the rule file may arrive after this one. Wait for it.
      var waited = 0;
      var boot = function () {
        if (window.OmdOfferRule) whenReady(start);
        else if ((waited += 50) < 10000) setTimeout(boot, 50);
      };
      boot();
    }
  }
})(typeof self !== 'undefined' ? self : this, function createEngine(env) {
  var DEBOUNCE = 400;
  var CHECKOUT_WAIT = 1500;
  var PROPERTY = '_offer';
  var KEYS_SECTION = 'offer-test-lines';

  // A page that has not left this long after checkout began never left.
  var LEAVE_RESET = 10000;

  var running = null;
  var rerun = false;
  var timer = null;
  var scheduled = false;
  // Checkout has begun: start no more writes, the page may go at any moment.
  var leaving = false;
  var leaveTimer = null;
  var waiting = [];
  var lock = env.lock || function (task) { return Promise.resolve().then(task); };
  // The cart lock's holder for the pass under way, if any.
  var holder = null;
  // While a cart request is stuck past the lock's ceiling: resolves once it
  // is done.
  var blocked = null;

  function enabled() {
    return !!env.config.active;
  }

  function url(path) {
    return env.root + path + (path.indexOf('?') === -1 ? '?' : '&') + 'omd_stamp=1';
  }

  function json(response) {
    return response.json().then(function (body) {
      if (!response.ok) throw new Error(body.description || body.message || 'cart request failed');
      return body;
    });
  }

  // Every cart request of a pass goes through the lock's holder, so the
  // lock knows what is in flight. Without a lock it just runs.
  function request(start) {
    return holder ? holder.request(start) : start();
  }

  function readCart() {
    return request(function () {
      return env.fetch(url('cart.js'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }).then(json);
    });
  }

  function post(path, body) {
    return request(function () {
      return env.fetch(url(path), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(json);
    });
  }

  // The painting line keys come from Liquid (product tag `dot-painting`).
  // `/cart.js` has no tags, and product type is wrong on .com accessories.
  function readPaintingKeys(cart) {
    var path = env.root + '?sections=' + KEYS_SECTION + '&omd_stamp=1';
    return request(function () {
      return env.fetch(path, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }).then(json);
    })
      .then(function (sections) {
        var match = /<script[^>]*id="offer-test-lines"[^>]*>([\s\S]*?)<\/script>/.exec(sections[KEYS_SECTION] || '');
        var data = match ? JSON.parse(match[1]) : null;
        // The section and the cart were read a moment apart. A different
        // item count means the cart moved in between: read again later.
        if (!data || data.count !== cart.item_count) return null;
        return data.keys;
      });
  }

  function current(item) {
    var value = item.properties && item.properties[PROPERTY];
    return value === null || value === undefined ? '' : String(value);
  }

  function isGift(item) {
    return !item.original_price || (item.properties && item.properties._Gifted);
  }

  function liveArm(cart) {
    var arm = cart.attributes && cart.attributes.offer_test_arm;
    if (!env.config.active || !arm) return '';
    if (env.rule.ARMS.indexOf(arm) === -1) return '';
    return (env.config.arms || []).indexOf(arm) === -1 ? '' : arm;
  }

  // The cart carries another arm than this device shows: a cart from the
  // test, before the offer went to A for everyone, and the arm script has
  // not written A yet.
  function staleArm(cart) {
    var device = env.config.active && env.arm && env.arm();
    if (!device || !device.arm || typeof device.ensureCartAttribute !== 'function') return null;
    var arm = cart.attributes && cart.attributes.offer_test_arm;
    return arm === device.arm ? null : device;
  }

  function withStamp(item, value, quantity) {
    var properties = {};
    Object.keys(item.properties || {}).forEach(function (name) { properties[name] = item.properties[name]; });
    properties[PROPERTY] = value;
    var body = { id: item.key, quantity: quantity, properties: properties };
    var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    if (plan) body.selling_plan = plan.id;
    return body;
  }

  // The next single write that moves the cart towards the wanted stamps, or
  // null when the cart is already right. Only differences are written.
  function nextWrite(cart, arm, keys) {
    var paintings = arm ? cart.items.filter(function (item) {
      return keys.indexOf(item.key) !== -1 && !isGift(item) && item.quantity > 0;
    }) : [];

    var wanted = arm ? env.rule.computeStamps(arm, paintings.map(function (item) {
      return { key: item.key, quantity: item.quantity, price: item.original_price, stamped: current(item) !== '' };
    })) : { value: '', stamps: {} };

    // Anything stamped that should not be: clear it by blanking. Never delete
    // the only property, Shopify ignores an empty `properties: {}`.
    var stale = cart.items.filter(function (item) {
      return current(item) !== '' && !wanted.stamps[item.key];
    })[0];
    if (stale) return { kind: 'set', body: withStamp(stale, '', stale.quantity) };

    // The rule stamps whole lines, so every line is stamped as it is.
    var lines = paintings.filter(function (item) { return wanted.stamps[item.key]; });
    for (var i = 0; i < lines.length; i++) {
      var item = lines[i];
      if (current(item) !== wanted.value) return { kind: 'set', body: withStamp(item, wanted.value, item.quantity) };
    }
    return null;
  }

  function write(step) {
    return post('cart/change.js', step.body);
  }

  function pass(held) {
    holder = held || null;
    var changed = false;
    var limit = 20;
    var armWritten = false;

    function step() {
      if (limit-- < 1) return Promise.resolve(changed);
      return readCart().then(function (cart) {
        if (!cart.items || !cart.items.length) return changed;
        // Stamps follow the arm on the cart. If that is not this device's
        // arm, write the device's arm first, inside this turn on the lock,
        // then read again. Once per pass: if it does not stick, the stamps
        // follow whatever arm the cart carries.
        var device = !armWritten && !leaving ? staleArm(cart) : null;
        if (device) {
          armWritten = true;
          changed = true;
          return Promise.resolve(device.ensureCartAttribute(holder)).then(step);
        }
        var arm = liveArm(cart);
        var needKeys = arm ? readPaintingKeys(cart) : Promise.resolve([]);
        return needKeys.then(function (keys) {
          if (keys === null) return step();
          var next = nextWrite(cart, arm, keys);
          if (!next || leaving) return changed;
          changed = true;
          return write(next).then(step);
        });
      });
    }
    var done = function () { holder = null; };
    return step().then(function (result) {
      done();
      return result;
    }, function (error) {
      done();
      // Tell the caller whether this pass wrote before it stopped.
      if (error) error.changed = changed;
      throw error;
    });
  }

  // A run is queued or under way.
  function busy() {
    return scheduled || !!running;
  }

  // Resolves once no run is queued or under way. The free-gift engine waits
  // on this, so it never decides on a cart the stamps are about to change.
  function idle() {
    if (!busy()) return Promise.resolve();
    return new Promise(function (resolve) { waiting.push(resolve); });
  }

  function wake() {
    if (busy()) return;
    var list = waiting;
    waiting = [];
    list.forEach(function (resolve) { resolve(); });
  }

  // One pass at a time, holding the shared cart lock. A change that lands
  // during a pass triggers one more.
  function run() {
    if (!enabled()) {
      wake();
      return Promise.resolve(false);
    }
    if (running) {
      rerun = true;
      return running;
    }
    running = lock(pass).then(function (changed) {
      if (changed) env.refreshCart();
      return changed;
    }, function (error) {
      // The lock let this pass go before it finished: run again, reading
      // the cart first. Redraw the drawer if it wrote before it stopped.
      if (error && error.released) rerun = true;
      // A cart request hung past the lock's ceiling while this run waited
      // its turn. It wrote nothing: try again once that request is done.
      if (error && error.gaveUp) {
        var settled = error.settled;
        blocked = settled;
        settled.then(function () {
          if (blocked === settled) blocked = null;
          schedule();
        });
      }
      if (error && error.changed) env.refreshCart();
      return false;
    }).then(function (changed) {
      running = null;
      if (rerun) {
        rerun = false;
        schedule();
      }
      wake();
      return changed;
    });
    return running;
  }

  // Test off: one cheap check per page load. Read the cart and blank any
  // stamp left over from when the test was on. Nothing else runs.
  function clearStamps() {
    return lock(pass).then(function (changed) {
      if (changed) env.refreshCart();
      return changed;
    }, function (error) {
      if (error && error.gaveUp) error.settled.then(clearStamps);
      return false;
    });
  }

  function schedule() {
    env.clearTimeout(timer);
    scheduled = true;
    timer = env.setTimeout(function () {
      scheduled = false;
      run();
    }, DEBOUNCE);
  }

  function bindPage(win, doc) {
    var bypass = false;

    doc.addEventListener('theme:cartchanged', schedule);
    win.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      leaving = false;
      schedule();
    });

    // The side cart's watcher calls schedule(). Where that drawer is not
    // running, watch cart requests here instead.
    env.setTimeout(function () {
      if (win.sideCart || !('PerformanceObserver' in win)) return;
      try {
        new win.PerformanceObserver(function (list) {
          list.getEntries().forEach(function (entry) {
            if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return;
            if (/omd_stamp=1/.test(entry.name)) return;
            if (/\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/.test(entry.name)) schedule();
          });
        }).observe({ type: 'resource', buffered: false });
      } catch (error) {}
    }, 1500);

    // The arm assignment makes sure the cart carries the arm. Wait for it
    // first, so the stamps are worked out from the right arm and no order
    // leaves without one. If that script is absent, carry on.
    function ensureArm() {
      return Promise.resolve().then(function () {
        var arm = win.omdOfferArm;
        return arm && typeof arm.ensureCartAttribute === 'function' ? arm.ensureCartAttribute() : null;
      }).catch(function () {}).then(ensurePageTest);
    }

    // The custom photo page test keeps its own cart attribute (snippets/pdp-v2-head.liquid).
    // Checkout waits for it the same way, so no order leaves without its version.
    function ensurePageTest() {
      return Promise.resolve().then(function () {
        var test = win.omdPdpCartAttr;
        return test && typeof test.ensure === 'function' ? test.ensure() : null;
      }).catch(function () {});
    }

    function beforeCheckout() {
      return ensureArm().then(run).then(function () {
        // The run gave up behind a cart request stuck past the lock's
        // ceiling. Stamp if that request is done within the cap below.
        return blocked ? blocked.then(run) : null;
      });
    }

    // One hold at a time, capped at CHECKOUT_WAIT. When the cap passes,
    // checkout goes on whatever happened: the shopper is never stuck here.
    var holding = false;

    function hold(next) {
      // Checkout is already on its way from an earlier click, and that click
      // stamped the cart. Go again without starting any write.
      if (leaving) {
        next();
        return;
      }
      holding = true;
      var finished = false;
      var finish = function () {
        if (finished) return;
        finished = true;
        holding = false;
        leaving = true;
        env.clearTimeout(leaveTimer);
        // Still here: the page never left. Catch up on what was skipped.
        leaveTimer = env.setTimeout(function () {
          leaving = false;
          schedule();
        }, LEAVE_RESET);
        next();
      };
      // The cap covers waiting for stamping. Nothing waits for the gift
      // engine's writes: the gift line does not change what the stamps charge.
      env.setTimeout(finish, CHECKOUT_WAIT);
      beforeCheckout().then(finish, finish);
    }

    // A modified or non-primary click is the shopper's own way of opening
    // checkout elsewhere (new tab, new window). Let it through untouched.
    function plain(event) {
      return event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey;
    }

    var passNextSubmit = false;

    doc.addEventListener('click', function (event) {
      if (!enabled() || !event.target.closest) return;

      // Side cart checkout button: stamp first, then go on to checkout.
      var button = event.target.closest('.side-cart__checkout-button');
      if (button) {
        if (!plain(event)) return;
        var drawer = button.closest('side-cart-drawer');
        var target = drawer && drawer.dataset.checkoutUrl;
        if (!target) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (holding) return;
        hold(function () { win.location.href = target; });
        return;
      }

      // The /cart page button: remember a click that must go straight through.
      var cartButton = event.target.closest('form[action$="/cart"] [name="checkout"]');
      if (cartButton) passNextSubmit = !plain(event);
    }, true);

    // The /cart page form: stamp first, then submit it again as it was.
    doc.addEventListener('submit', function (event) {
      var form = event.target;
      var submitter = event.submitter;
      if (!enabled() || !form.matches || !form.matches('form[action$="/cart"]')) return;
      if (!submitter || submitter.name !== 'checkout') return;
      if (bypass) return;
      if (passNextSubmit) {
        passNextSubmit = false;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (holding) return;
      hold(function () {
        bypass = true;
        if (form.requestSubmit) form.requestSubmit(submitter);
        else form.submit();
      });
    }, true);
  }

  return {
    run: run,
    idle: idle,
    clearStamps: clearStamps,
    schedule: schedule,
    bindPage: bindPage,
    pass: pass,
    nextWrite: nextWrite
  };
});
